import {useFieldArray, useFormContext} from "react-hook-form";
import React, {memo, useEffect, useState} from "react";
import StackHorizontal from "../../../Framework/Components/Wrappers/StackHorizontal";
import {ColourMod, FlexAlign, FlexGap, FlexJustify} from "../../../Framework/Data/Enums/LayoutEnums";
import {ButtonSubmit} from "../../../Framework/Components/Buttons/Button";
import StackVertical from "../../../Framework/Components/Wrappers/StackVertical";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faRectangleHistory} from "@fortawesome/pro-solid-svg-icons";
import ValidationPatterns from "../../../Framework/RegEx";
import AddressImporterRow from "./AddressImporterRow";
import {checkPhoneNumberValid} from "../../../Api/UsersEndpoints";
import GenericLoader from "../../../Framework/Components/Loaders/GenericLoader";
import { GetObjectIndexByKeyValue } from '../../../Framework/Constants/Helpers';
import { CountryCodes } from '../../../Framework/Data/CountryCodes';

type AddressImporterResultsType = {
    addressData: any;
    embeddedForm?: boolean;
    removeCallback?: any;
}

const AddressImporterResults = (props: AddressImporterResultsType) => {
    const [ addressData, setAddressData ] = useState<any>(null);
    const { control, formState, setError } = useFormContext();

    const [ submitBlocker, setSubmitBlocker ] = useState<boolean>(false);

    const { fields, remove, append } = useFieldArray({
        control,
        name: `Addresses`,
        shouldUnregister: true
    });

    const HandleRemove = (id: number) => {
        remove(id);
        if(fields.length === 1) {
            setAddressData(null);
            props.removeCallback(fields.length);
        }
    }

    useEffect(() => {
        if(addressData) {
            remove();
            SortAddressData().then(() => null);
        }

    }, [addressData]);

    useEffect(() => {
        if(props.addressData) {
            setAddressData(props.addressData);
        }
    }, [props]);

    const SortAddressData = async () => {
        let invalidRows: Array<any> = [];
        let validRows: Array<any> = [];

        await Promise.all(addressData?.map((field: any, fieldIndex: number) => {
            const phoneNo = field.phoneNo.replace(/ /g, '').replace(/-/g, '').replace(/\(/g, '').replace(/\)/g, '').trim();
            const countryEntry = field.countryCode ? CountryCodes[GetObjectIndexByKeyValue(CountryCodes, 'value', CountryCodeFixup(field.countryCode, field.countryName))] : CountryCodes[GetObjectIndexByKeyValue(CountryCodes, 'label', field.countryName)];
            const newAddressData = {...field, phoneNo: phoneNo, countryCode: countryEntry?.value, countryName: countryEntry?.label};

            return RowDataValid(newAddressData).then((valid: boolean) => {
                if(valid) {
                    validRows.push({...newAddressData, validRow: true});
                } else {
                    invalidRows.push({...newAddressData, validRow: false});
                }
                return true;
            })
        })).then(() => {
            [...invalidRows, ...validRows].forEach((item: any, index: number) => {
                append({...item})
            })
        });
    }

    const CountryCodeFixup = (code: string, name: string) => {
        let countryCode = code.toLowerCase().replace(/'/g, '');
        let codeName = name.toLowerCase().replace(/'/g, '');

        if (countryCode === 'uk') {
            countryCode = 'GB';
        }

        if (codeName === 'united kingdom' || codeName === 'england' || codeName === 'scotland' ||
            codeName === 'wales' || codeName === 'northern ireland') {
            countryCode = 'GB';
        }

        return countryCode.toUpperCase();
    }

    const RowDataValid = async (data: any) => {
        const emailValid = ValidationPatterns.email.test(data.emailAddress);
        const phoneValid = ValidationPatterns.telephone.test(data.phoneNo) && await checkPhoneNumberValid(data.phoneNo);

        return !!data.name && !!data.addressLine1 && !!data.addressLine2 && !!data.postCode && !!data.countryCode &&
            emailValid && phoneValid
    }

    return (
        <>
            {fields.map((field: any, fieldIndex: number) =>
                <AddressImporterRow
                    {...field}
                    key={field.id}
                    fieldIndex={fieldIndex}
                    removeCallback={(id: number) => HandleRemove(id)}
                    embedded={props.embeddedForm}
                    lookUpsActive={(e) => setSubmitBlocker(e)}
                />
            )}

            {fields.length > 0 && (
                <div className={`sticky sticky-bottom p-md secondary mt-sm ${props.embeddedForm ? 'round' : ''}`}>
                    <StackHorizontal align={FlexAlign.Center} justify={FlexJustify.Between}>
                        <div>{fields.length} Addresses to Import</div>
                        <ButtonSubmit title={'Import'} colour={ColourMod.Primary} disabled={Object.keys(formState.errors).length !== 0 || submitBlocker}/>
                    </StackHorizontal>
                </div>
            )}

            {!addressData && (
                <StackVertical gap={FlexGap.ExtraSmall} align={FlexAlign.Center} justify={FlexJustify.Center} extraClass={'m-xxl p-xxl text-center'}>
                    <FontAwesomeIcon icon={faRectangleHistory} size={"3x"} />
                    <h6 className={'sub-heading mt-sm mb-xs'}>No Data Loaded</h6>
                </StackVertical>
            )}

            {addressData && fields.length === 0 && (
                <StackVertical gap={FlexGap.ExtraSmall} align={FlexAlign.Center} justify={FlexJustify.Center} extraClass={'m-xxl p-xxl text-center'}>
                    <GenericLoader label={'Data'} />
                </StackVertical>
            )}
        </>
    )
}

export default AddressImporterResults
